import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useNavigate } from 'react-router-dom';
import { withFileUploader } from '../../hoc/withFileUploader';
import { Container } from '../../../ui/gridSystem';
import {
  PageTitle,
  InputFile,
  PicWrapper,
  InfoBlockContainer,
  InfoBlock,
  UserInfo,
  TitleAndProfile,
} from './style';
import { CircleImage, Text, CTA, Inline } from '../../../ui/atoms';
import Icon from '../../../ui/atoms/Icon';
import { apiHost, baseUrl } from '../../api/config';
import { AUTH_LOGOUT, MODAL_OPEN } from '../../redux/actions';
import { UPDATE_USER_PICTURE } from '../../redux/actions/user';
import CardNewsletter from '../../../ui/atoms/Cards/CardNewsletter';
import { NEWSLETTER_GET } from '../../redux/actions/newsletter';
import 'react-toggle/style.css';
import ModalPwdChange, {
  ModalPwdChangeId,
} from '../../../ui/atoms/Modals/ModalPwdChange';
import ModalGeneralSettings, {
  ModalGeneralSettingsId,
} from '../../../ui/atoms/Modals/ModalGeneralSettings';
import { getLabelByRole } from '../../../utils/labels';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import Pagination from '../../../ui/atoms/Pagination';
import { Legend } from '../../../ui/atoms/Cards/CardNewsletter/style';
import theme from '../../../ui/theme';
import CustomTooltip from '../../../ui/atoms/CustomTooltip';

const acceptedFiles = ['.jpg', '.jpeg', '.png'];

const EditProfile = ({
  mediaIsPhone,
  user,
  onFileChange,
  updateUserPic,
  getNewsletterList,
  newsletterList,
  pager,
  modalOpen,
  startLogout,
}) => {
  const navigate = useNavigate();
  const [updatedUrl, setUpdatedUrl] = useState();

  useEffect(() => {
    window.scrollTo(0, 0);
    getNewsletterList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clickOnInput = () => {
    const input = document.querySelector('input[type=file]');
    input.click();
  };

  const getUrl = (_updatedFile, data) => {
    const newUrl = data?.url;
    if (newUrl) {
      setUpdatedUrl(newUrl);
      updateUserPic(newUrl);
    }
  };

  const logout = () => {
    startLogout(navigate);
  };

  const loadMore = (page) => getNewsletterList({ page });
  const setItemsPerPage = (v) =>
    getNewsletterList({ items_per_page: v?.value });

  const isExpertOnly = useCallback(() => {
    const { isTutor, isCotutor, isSupervisor, isSubjectExpert, isAdmin } = user;
    return (
      isSubjectExpert && !(isTutor || isCotutor || isSupervisor || isAdmin)
    );
  }, [user]);

  return (
    <>
      <Container>
        <TitleAndProfile>
          <PageTitle>
            <Text type="h1" color="#1A1A1A">
              Il mio profilo
            </Text>
            {!isExpertOnly() && (
              <CTA
                type="tertiary"
                btnText={mediaIsPhone ? '' : 'Impostazioni generali'}
                icon="settings-01"
                onClick={() => modalOpen(ModalGeneralSettingsId)}
              />
            )}
          </PageTitle>
          <UserInfo>
            <InfoBlockContainer>
              <PicWrapper>
                <CircleImage
                  src={updatedUrl || user?.profile_pic}
                  size={mediaIsPhone ? 118 : 140}
                  name={user}
                  onClick={clickOnInput}
                />
              </PicWrapper>
              <InputFile
                type="file"
                id="uploadFile"
                accept={acceptedFiles.join(',')}
                onChange={() => {
                  const fileToUpload =
                    document.querySelector('input[type=file]').files[0];
                  onFileChange({
                    file: fileToUpload,
                    url: `${apiHost}${baseUrl}pzzl_auth/user/pic/upload`,
                    callback: getUrl,
                  });
                }}
              />
              <InfoBlock>
                <Text type="h1.5" color="#1F1F1F">
                  {user?.first_name} {user?.last_name}
                </Text>
                <Text type="h3" color="#515151">
                  {user?.ffoo?.value}
                </Text>
                {user?.lms_roles?.length > 0 && (
                  <Text type="h3.5" color="#646464">
                    <span />
                    {(user?.lms_roles || []).map((role, i) => (
                      <span key={`role-${i}`}>
                        {i > 0 ? ', ' : ''}
                        {getLabelByRole(role)}
                      </span>
                    ))}
                  </Text>
                )}
                <div className="btns-wrapper">
                  {user?.credentials && (
                    <CTA
                      type="tertiary"
                      btnText="Cambia password"
                      icon="chevron-right"
                      onClick={() => modalOpen(ModalPwdChangeId)}
                    />
                  )}
                  <CTA
                    type="tertiary"
                    btnText="Logout"
                    icon="log-out-01"
                    onClick={logout}
                  />
                </div>
              </InfoBlock>
              <InfoBlock>
                <Text type="h2" color="#1F1F1F">
                  Info personali
                </Text>
                <Text type="b4">
                  Divisione:{' '}
                  <b>{user?.work_division?.value || 'Non disponibile'}</b>
                </Text>
                <Text type="b4">
                  Ruolo: <b>{user?.work_role?.value || 'Non disponibile'}</b>
                </Text>
                <Text type="b4">
                  Sede:{' '}
                  {user?.work_city || user?.work_address ? (
                    <b>
                      {user?.work_city}
                      {user?.work_city && user?.work_address ? ', ' : ''}
                      {user?.work_address ? `${user?.work_address}` : ''}
                    </b>
                  ) : (
                    <b>Non disponibile</b>
                  )}
                </Text>
                <div className="d-flex">
                  <Text type="b4">
                    Matricola: <b>{user?.matricola || 'Non disponibile'}</b>
                  </Text>
                  <CustomTooltip
                    type="primary"
                    title="Matricola"
                    tooltipKey="matricola_info"
                  />
                </div>
              </InfoBlock>
              <InfoBlock>
                <Text type="h2" color="#1F1F1F">
                  Contatti
                </Text>
                <Inline columnGap="4px" alignItems="baseline">
                  <Icon
                    type="mail-02"
                    stroke={theme().colors.textPrimary}
                    size={14.5}
                    strokeWidth={2}
                    clickable={false}
                  />
                  <Text type="b4">
                    Email: <b>{user?.email}</b>
                  </Text>
                </Inline>
              </InfoBlock>
            </InfoBlockContainer>
          </UserInfo>
        </TitleAndProfile>
        {newsletterList?.length > 0 && (
          <section>
            <Text type="h2" mb={32}>
              Newsletter
            </Text>
            {!mediaIsPhone && (
              <Legend>
                <Text type="statistics-heading">Titolo</Text>
                <div />
                <Text type="statistics-heading">Descrizione</Text>
              </Legend>
            )}
            <div className="pb-4">
              {newsletterList.map((item, index) => (
                <CardNewsletter key={`newsletter-${index}`} item={item} />
              ))}
            </div>
            <Pagination
              pager={pager}
              callBack={loadMore}
              setItemsPerPage={setItemsPerPage}
            />
          </section>
        )}
      </Container>
      <ModalPwdChange />
      {!isExpertOnly() && <ModalGeneralSettings />}
    </>
  );
};

EditProfile.propTypes = {
  mediaIsPhone: PropTypes.bool,
  // state
  user: PropTypes.object,
  newsletterList: PropTypes.array,
  pager: PropTypes.object,

  // dispatch
  updateUserPic: PropTypes.func,
  getNewsletterList: PropTypes.func,

  // HOC
  onFileChange: PropTypes.func.isRequired,
  modalOpen: PropTypes.func,
  startLogout: PropTypes.func,
};

export default compose(
  connect(
    (state) => {
      const { user } = state;
      const { list: newsletterList, pager } = state.newsletter;

      return {
        user,
        newsletterList,
        pager,
      };
    },
    (dispatch) => ({
      updateUserPic: (url) => dispatch({ type: UPDATE_USER_PICTURE, url }),
      getNewsletterList: (filters) =>
        dispatch({ type: NEWSLETTER_GET._REQUEST, filters }),
      modalOpen: (id) => dispatch({ type: MODAL_OPEN, id }),
      startLogout: (navigate) =>
        dispatch({ type: AUTH_LOGOUT._REQUEST, navigate }),
    })
  ),
  withFileUploader
)(withMediaQueries(EditProfile));
